import { Link, useRouterState } from '@tanstack/react-router'
import { ChevronDown, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { useState, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useOptionalModuleNavFlags } from '@/shared/hooks/useOptionalModuleNavFlags'
import { useTombolaEnabled } from '@/shared/hooks/useTombolaEnabled'
import { iconForHref } from '@/shared/lib/moduleIcons'
import { defaultPortalNavigation, projectPortalNavigation } from '@/shared/lib/portalNavigation'
import { portalNavAriaCurrentPage } from '@/shared/lib/portalNavAriaCurrent'
import { cn } from '@/shared/lib/utils'
import type { PortalNavigationItem } from '@/shared/schemas/brand.schema'

function injectTombola(items: PortalNavigationItem[], label: string): PortalNavigationItem[] {
  if (items.some((item) => item.id === 'nav-tombola' || item.href === '/lottery/tombola')) return items

  return items.map((item) => {
    const children = injectTombola(item.children, label)
    const lotteryIndex = children.findIndex((child) => child.href === '/lottery')
    if (lotteryIndex < 0 || children.some((child) => child.id === 'nav-tombola' || child.href === '/lottery/tombola')) {
      return { ...item, children }
    }

    const nextChildren = [...children]
    nextChildren.splice(lotteryIndex + 1, 0, {
      id: 'nav-tombola',
      label,
      href: '/lottery/tombola',
      module_slug: 'lottery',
      sort: children[lotteryIndex].sort + 1,
      enabled: true,
      link_type: 'internal',
      placement: item.placement,
      children: [],
    })

    return { ...item, children: nextChildren }
  })
}

function activeGroupIds(items: PortalNavigationItem[], pathname: string): string[] {
  return items
    .filter((item) => item.children.some((child) => portalNavAriaCurrentPage(pathname, child.href)))
    .map((item) => item.id)
}

export function PortalSidebar({
  headerConfigured,
  collapsed,
  onToggle,
  className,
}: {
  headerConfigured: PortalNavigationItem[] | null
  collapsed: boolean
  onToggle: () => void
  className?: string
}) {
  const { t } = useTranslation()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const tombolaEnabled = useTombolaEnabled()
  const moduleFlags = useOptionalModuleNavFlags()
  const source = headerConfigured === null || headerConfigured.length === 0 ? defaultPortalNavigation(t) : headerConfigured
  const projected = projectPortalNavigation(source, moduleFlags)
  const items = tombolaEnabled ? injectTombola(projected, t('lottery.tombola.nav_tombola')) : projected
  const [menuState, setMenuState] = useState<{ pathname: string; openGroups: Set<string> }>(() => ({
    pathname,
    openGroups: new Set(activeGroupIds(items, pathname)),
  }))
  const openGroups = menuState.pathname === pathname ? menuState.openGroups : new Set<string>()

  const toggleGroup = (id: string) => {
    setMenuState((current) => {
      const next = new Set(current.pathname === pathname ? current.openGroups : [])
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return { pathname, openGroups: next }
    })
  }

  const focusSubmenuLink = (submenuId: string, last = false) => {
    requestAnimationFrame(() => {
      const links = document.getElementById(submenuId)?.querySelectorAll<HTMLElement>('a[href]')
      links?.[last ? links.length - 1 : 0]?.focus()
    })
  }

  const handleGroupKeyDown = (event: KeyboardEvent<HTMLButtonElement>, itemId: string, submenuId: string) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      setMenuState((current) => ({
        pathname,
        openGroups: new Set([...(current.pathname === pathname ? current.openGroups : []), itemId]),
      }))
      focusSubmenuLink(submenuId, event.key === 'ArrowUp')
    } else if (event.key === 'Escape') {
      event.preventDefault()
      setMenuState({ pathname, openGroups: new Set() })
    }
  }

  const handleSubmenuLinkKeyDown = (event: KeyboardEvent<HTMLAnchorElement>, itemId: string) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      setMenuState((current) => {
        const next = new Set(current.pathname === pathname ? current.openGroups : [])
        next.delete(itemId)
        return { pathname, openGroups: next }
      })
      document.getElementById(`portal-sidebar-${itemId}-toggle`)?.focus()
      return
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return

    const links = [...(event.currentTarget.closest('ul')?.querySelectorAll<HTMLElement>('a[href]') ?? [])]
    if (links.length === 0) return

    event.preventDefault()
    const step = event.key === 'ArrowDown' ? 1 : -1
    links[(links.indexOf(event.currentTarget) + step + links.length) % links.length]?.focus()
  }

  const srExternal = t('app.layout.external_link_sr')
  const itemBase =
    'flex min-h-10 items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-foreground no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring aria-[current=page]:bg-accent aria-[current=page]:text-accent-foreground'

  const renderLink = (
    item: PortalNavigationItem,
    nested = false,
    onKeyDown?: (event: KeyboardEvent<HTMLAnchorElement>) => void,
  ) => {
    const Icon = iconForHref(item.href)
    const content = (
      <>
        <Icon className={cn('size-5 shrink-0', nested && !collapsed && 'size-4')} aria-hidden />
        <span className={cn('truncate', collapsed && 'sr-only')}>{item.label}</span>
      </>
    )
    const title = collapsed ? item.label : item.link_title?.trim() || undefined
    const className = cn(itemBase, nested && !collapsed && 'ms-5 text-sm')

    if (item.link_type === 'external') {
      const target = item.target?.trim() || '_blank'
      const rel = item.rel?.trim() || (target === '_blank' ? 'noopener noreferrer' : undefined)
      return (
        <a href={item.href} title={title} target={target} rel={rel} className={className} onKeyDown={onKeyDown}>
          {content}
          <span className="sr-only">{srExternal}</span>
        </a>
      )
    }

    return (
      <Link
        to={item.href}
        title={title}
        target={item.target?.trim() || undefined}
        className={className}
        aria-current={portalNavAriaCurrentPage(pathname, item.href) ? 'page' : undefined}
        onKeyDown={onKeyDown}
      >
        {content}
      </Link>
    )
  }

  return (
    <aside
      data-slot="portal-sidebar"
      className={cn(
        'sticky top-0 flex h-dvh shrink-0 flex-col border-r border-border bg-card',
        collapsed ? 'w-16' : 'w-64',
        className,
      )}
    >
      <nav aria-label={t('app.layout.main_nav')} className="flex-1 overflow-y-auto p-2">
        <ul className="flex flex-col gap-1">
          {items.map((item) => {
            if (item.children.length === 0) return <li key={item.id}>{renderLink(item)}</li>

            const open = openGroups.has(item.id)
            const submenuId = `portal-sidebar-${item.id}`
            const Icon = iconForHref(item.children[0]?.href ?? item.href)

            return (
              <li key={item.id}>
                <div className="flex items-center gap-1">
                  {item.link_type === 'group' ? (
                    <button
                      id={`${submenuId}-toggle`}
                      type="button"
                      className={cn(itemBase, 'min-w-0 flex-1')}
                      aria-expanded={open}
                      aria-controls={submenuId}
                      title={collapsed ? item.label : undefined}
                      onClick={() => toggleGroup(item.id)}
                      onKeyDown={(event) => handleGroupKeyDown(event, item.id, submenuId)}
                    >
                      <Icon className="size-5 shrink-0" aria-hidden />
                      <span className={cn('truncate', collapsed && 'sr-only')}>{item.label}</span>
                      <ChevronDown className={cn('ms-auto size-4 shrink-0 transition-transform', open && 'rotate-180')} aria-hidden />
                    </button>
                  ) : (
                    <>
                      <div className="min-w-0 flex-1">{renderLink(item)}</div>
                      <button
                        id={`${submenuId}-toggle`}
                        type="button"
                        className="flex size-10 shrink-0 items-center justify-center rounded-md hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        aria-expanded={open}
                        aria-controls={submenuId}
                        aria-label={t('app.layout.submenu_toggle', { label: item.label })}
                        onClick={() => toggleGroup(item.id)}
                        onKeyDown={(event) => handleGroupKeyDown(event, item.id, submenuId)}
                      >
                        <ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} aria-hidden />
                      </button>
                    </>
                  )}
                </div>
                {open ? (
                  <ul id={submenuId} className="mt-1 flex flex-col gap-1">
                    {item.children.map((child) => (
                      <li key={child.id}>
                        {renderLink(child, true, (event) => handleSubmenuLinkKeyDown(event, item.id))}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            )
          })}
        </ul>
      </nav>

      <div className="border-t border-border p-2">
        <button type="button" onClick={onToggle} aria-pressed={collapsed} className={cn(itemBase, 'w-full')}>
          {collapsed ? <PanelLeftOpen className="size-5 shrink-0" aria-hidden /> : <PanelLeftClose className="size-5 shrink-0" aria-hidden />}
          <span className={cn('truncate', collapsed && 'sr-only')}>
            {collapsed ? t('app.layout.sidebar_expand') : t('app.layout.sidebar_collapse')}
          </span>
        </button>
      </div>
    </aside>
  )
}
